import { useCallback, useEffect, useState } from 'react'
import {
  Badge,
  Fab,
  Grid,
  IconButton,
  Link,
  ListItemIcon,
  makeStyles,
  Menu,
  MenuItem,
  Tooltip,
  Typography
} from '@material-ui/core'
import AlertIcon from '@material-ui/icons/NotificationsActiveOutlined'
import DoneIcon from '@material-ui/icons/Done'
import {
  downloadDocument,
  getNotificationsByProvider,
  markNotificationAsRead,
  markNotificationsAsRead
} from '../../api/notifications.service'
import { useSelector } from 'react-redux'
import { formatDateTime } from '../../common/utils'
import { OpenInNewOutlined } from '@material-ui/icons'

const useStyles = makeStyles(() => ({
  alertDate: {
    textAlign: 'right',
    fontSize: '0.6rem'
  },
  alertDescription: {
    fontSize: '0.75rem'
  },
  alertContainer: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '100%',
    borderWidth: '1px 0px 0px 0px',
    borderStyle: 'solid',
    borderColor: 'lightgray'
  }
}))

const Alert = () => {
  const classes = useStyles()
  const [alerts, setAlerts] = useState([])
  const [alertCounter, setAlertCounter] = useState(0)
  const [anchorEl, setAnchorEl] = useState(null)
  const open = Boolean(anchorEl)
  const providerSelected = useSelector((state) => state.provider)

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget)
  }
  const handleClose = () => {
    setAnchorEl(null)
  }

  const getData = useCallback(() => {
    if (providerSelected.id !== undefined)
      getNotificationsByProvider(providerSelected.id).then(({ data }) => {
        setAlerts(data)
        setAlertCounter(data.length)
      })
  }, [providerSelected.id])

  useEffect(() => {
    getData()
  }, [getData])

  const markAllAsRead = () => {
    handleClose()
    markNotificationsAsRead(providerSelected.id).then(() => {
      setAlerts([])
      setAlertCounter(0)
    })
  }

  const markAsRead = (alert) => {
    handleClose()
    markNotificationAsRead(alert.id).then(() => {
      setAlerts(alerts.filter((ele) => ele.id !== alert.id))
      setAlertCounter(alertCounter - 1)
    })
  }

  const download = async (alert) => {
    const response = await downloadDocument(alert.id)
    window.open(response.data)
  }

  return (
    <div>
      <Tooltip title="Notificaciones">
        {alertCounter > 0 ? (
          <Badge badgeContent={alertCounter} color="secondary" className="mr-3">
            <IconButton
              id="notificationButton"
              onClick={handleClick}
              size="small"
              sx={{ ml: 2 }}
              aria-controls={open ? 'alert-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={open ? 'true' : undefined}
              color="secondary"
            >
              <AlertIcon />
            </IconButton>
          </Badge>
        ) : (
          <IconButton
            onClick={handleClick}
            size="small"
            sx={{ ml: 2 }}
            aria-controls={open ? 'alert-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={open ? 'true' : undefined}
            color="secondary"
            className="mr-2"
          >
            <AlertIcon />
          </IconButton>
        )}
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        id="alert-menu"
        open={open}
        onClose={handleClose}
        PaperProps={{
          elevation: 2,
          style: {
            maxHeight: '364px',
            width: '40ch'
          }
        }}
        MenuListProps={{
          disablePadding: true
        }}
      >
        {alertCounter > 0 ? (
          <>
            <Grid item xs={12} className="bg-light">
              <Link className="w-100" component="button" onClick={() => markAllAsRead()}>
                <Typography align="center" color="secondary" noWrap={true} className="p-2">
                  Marcar todas como leídas
                </Typography>
              </Link>
            </Grid>
            {alerts.map((alert, index) => {
              return (
                <Grid key={`alert-${index}`} container className={`p-3 ${classes.alertContainer}`}>
                  <Grid item xs={12}>
                    <Typography color="primary" className={classes.alertDate}>
                      {formatDateTime(alert.date)}
                    </Typography>
                  </Grid>
                  <Grid item xs={2}>
                    <ListItemIcon>
                      <Tooltip title="Marcar como leído">
                        <Fab
                          color="secondary"
                          size="small"
                          component="span"
                          onClick={() => markAsRead(alert)}
                        >
                          <DoneIcon />
                        </Fab>
                      </Tooltip>
                    </ListItemIcon>
                  </Grid>
                  <Grid item xs={10}>
                    <Grid container direction="column" spacing={2} style={{ maxWidth: '300px' }}>
                      <Grid item xs={12}>
                        <Typography color="secondary" noWrap={true}>
                          {alert.title}
                        </Typography>
                        <Typography
                          className={classes.alertDescription}
                          style={{ whiteSpace: 'break-spaces' }}
                        >
                          {alert.body}
                        </Typography>
                      </Grid>
                    </Grid>
                    {alert.hasDocument && (
                      <Grid item xs={12}>
                        <Tooltip title="Abrir documento en un nueva pestaña">
                          <Link
                            color="secondary"
                            size="small"
                            component="button"
                            onClick={() => download(alert)}
                          >
                            <div className="d-flex align-items-center">
                              <Typography color="primary" variant="caption">
                                Abrir documento
                              </Typography>
                              <OpenInNewOutlined />
                            </div>
                          </Link>
                        </Tooltip>
                      </Grid>
                    )}
                  </Grid>
                </Grid>
              )
            })}
          </>
        ) : (
          <MenuItem disabled>
            <Typography className="my-2" color="secondary" noWrap={true}>
              No hay nuevas notificaciones
            </Typography>
          </MenuItem>
        )}
      </Menu>
    </div>
  )
}

export default Alert
